import { useEffect, useMemo, useState } from 'react';
import { FiEdit3, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { EmailFieldHint, EmailFieldLabel } from '../_UI/EmailFieldGuide';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { useEmailEditorNavigation } from '../_Hooks/useEmailEditorNavigation';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const EMPTY = { name: '', category: '', subject: '', body_text: '', body_html: '', active: true };
const norm = (value = '') => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const EmailTemplatesView = () => {
    const api = useEmailAdmin();
    const { editorRef, goToEditor } = useEmailEditorNavigation();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null);
    const [order, setOrder] = useState('updated-desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState(EMPTY);
    const [message, setMessage] = useState('');

    const load = async () => {
        try { setItems(await api.get(C.TEMPLATES) || []); setMessage(''); }
        catch (err) { setMessage(err.message); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const startCreate = () => { setEditing(null); setValues(EMPTY); setFormOpen(true); setMessage(''); goToEditor(); };
    const startEdit = (item) => { setEditing(item); setValues({ ...EMPTY, ...item }); setFormOpen(true); setMessage(''); goToEditor(); };
    const submit = async (event) => {
        event.preventDefault();
        try {
            await api.securePost(C.TEMPLATE_SAVE, { ...values, id: editing?.id || undefined, active: values.active ? 1 : 0 });
            setFormOpen(false); setEditing(null); await load();
        } catch (err) { setMessage(err.message); }
    };
    const remove = async (item) => {
        if (!(await confirm.danger(`Eliminar la plantilla "${item.name}"?`, 'Esta accion no se puede deshacer.'))) return;
        try { await api.securePost(C.TEMPLATE_REMOVE, { id: item.id }); await load(); }
        catch (err) { setMessage(err.message); }
    };

    const categories = useMemo(() => [...new Set(items.map((item) => item.category).filter(Boolean))].map((value) => ({ value, label: value })), [items]);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const filtered = useMemo(() => {
        const term = norm(search);
        const active = activeFilter?.value ?? activeFilter;
        const category = categoryFilter?.value ?? categoryFilter;
        const rows = items.filter((item) => {
            if (term && !norm(`${item.name} ${item.category} ${item.subject} ${item.body_text}`).includes(term)) return false;
            if (active !== null && active !== undefined && active !== '' && String(item.active ? 1 : 0) !== String(active)) return false;
            if (category && item.category !== category) return false;
            return true;
        });
        return [...rows].sort((a, b) => order === 'name-asc' ? String(a.name).localeCompare(String(b.name)) : new Date(b.updated_at) - new Date(a.updated_at));
    }, [items, search, activeFilter, categoryFilter, order]);

    useEffect(() => { setPage(1); }, [search, activeFilter, categoryFilter, order, perPage]);
    const totalPages = perPage === -1 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const visible = perPage === -1 ? filtered : filtered.slice((safePage - 1) * perPage, safePage * perPage);
    const from = filtered.length ? (perPage === -1 ? 1 : ((safePage - 1) * perPage) + 1) : 0;
    const to = filtered.length ? (perPage === -1 ? filtered.length : Math.min(safePage * perPage, filtered.length)) : 0;

    return (
        <EmailSectionLayout title="Plantillas" description="Guarda mensajes reutilizables para CV, seguimiento, presentación o cualquier contacto profesional.">
            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__collection_tools PBMAIL__collection_tools">
                    <TableHeader
                        pill="PLANTILLAS"
                        title="Biblioteca de"
                        titleAccent="mensajes"
                        newLabel="Nueva plantilla"
                        onNew={startCreate}
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Buscar nombre, categoría, asunto o contenido…"
                        orderOptions={[{ value: 'updated-desc', label: 'Actualizadas recientes' }, { value: 'name-asc', label: 'Nombre A → Z' }]}
                        order={order}
                        onOrder={setOrder}
                        filters={[
                            { key: 'active', label: 'Estado', type: 'select', value: activeFilter, onChange: setActiveFilter, placeholder: 'Todos', options: [{ value: '1', label: 'Activa' }, { value: '0', label: 'Inactiva' }] },
                            { key: 'category', label: 'Categoría', type: 'select', value: categoryFilter, onChange: setCategoryFilter, placeholder: 'Todas', options: categories },
                        ]}
                        onClearFilters={() => { setSearch(''); setActiveFilter(null); setCategoryFilter(null); }}
                        stats={<div className="PBADMIN__filter_summary"><span><strong>{filtered.length}</strong> resultado{filtered.length === 1 ? '' : 's'}</span><span>{items.length} total</span></div>}
                    />
                </div>

                {message && <div className="PBADMIN__alert">{message}</div>}

                {formOpen && (
                    <form ref={editorRef} className="PBADMIN__editor PBADMIN__form_grid PBMAIL__campaign_form PBMAIL__editor" onSubmit={submit}>
                        <div className="PBADMIN__editor_head"><strong>{editing ? 'Editar plantilla' : 'Nueva plantilla'}</strong><button type="button" onClick={() => setFormOpen(false)}><FiX /></button></div>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="required">Nombre</EmailFieldLabel>
                            <input required placeholder="Ej: Presentacion CV Backend" value={values.name || ''} onChange={(e) => setValues((p) => ({ ...p, name: e.target.value }))} />
                            <EmailFieldHint>Es solo para identificar la plantilla dentro del panel.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="optional">Categoria</EmailFieldLabel>
                            <input placeholder="Ej: CV, seguimiento, presentacion" value={values.category || ''} onChange={(e) => setValues((p) => ({ ...p, category: e.target.value }))} />
                            <EmailFieldHint>Te sirve para ordenar la biblioteca de mensajes.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field PBADMIN__field--2">
                            <EmailFieldLabel status="required">Asunto</EmailFieldLabel>
                            <input required placeholder="Ej: Desarrollador Backend - CV Gaston Bailador" value={values.subject || ''} onChange={(e) => setValues((p) => ({ ...p, subject: e.target.value }))} />
                            <EmailFieldHint>Se copia a la campana cuando aplicas esta plantilla.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field PBADMIN__field--2">
                            <EmailFieldLabel status="required">Mensaje</EmailFieldLabel>
                            <textarea rows="12" required={!values.body_html} value={values.body_text || ''} onChange={(e) => setValues((p) => ({ ...p, body_text: e.target.value }))} placeholder={'Hola {{nombre}},\n\nSoy Gaston Bailador, desarrollador de software. Te comparto mi CV para futuras oportunidades en {{empresa}}.\n\nGracias por tu tiempo.\nSaludos.'} />
                            <EmailFieldHint>Podes usar variables para que cada destinatario reciba un texto personalizado.</EmailFieldHint>
                        </label>
                        <div className="PBMAIL__variables PBADMIN__field--2"><strong>Variables disponibles</strong><code>{'{{nombre}}'}</code><code>{'{{apellido}}'}</code><code>{'{{nombre_completo}}'}</code><code>{'{{empresa}}'}</code><code>{'{{email}}'}</code><small>Ejemplo: Hola {'{{nombre}}'} &rarr; Hola Maria.</small></div>
                        <div className="PBMAIL__check_group PBADMIN__field--2">
                            <label className="PBADMIN__check"><input type="checkbox" checked={!!values.active} onChange={(e) => setValues((p) => ({ ...p, active: e.target.checked }))} /><span>Plantilla activa</span></label>
                            <EmailFieldHint>Dejala activa si queres que aparezca al crear o editar campanas.</EmailFieldHint>
                        </div>
                        <div className="PBADMIN__form_footer"><button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiSave /> Guardar plantilla</button><button type="button" className="PBADMIN__secondary" onClick={() => setFormOpen(false)}>Cancelar</button></div>
                    </form>
                )}

                <div className="PBMAIL__template_grid">
                    {visible.length === 0 && !api.isLoading && <div className="PBADMIN__empty">No hay plantillas para mostrar.</div>}
                    {visible.map((item) => (
                        <article className="PBMAIL__template_card" key={item.id}>
                            <div className="PBMAIL__template_top"><div><span className={`PBMAIL__status is-${item.active ? 'active' : 'inactive'}`}>{item.active ? 'Activa' : 'Inactiva'}</span><h3>{item.name}</h3><small>{item.category || 'Sin categoría'}</small></div><div className="PBMAIL__campaign_card_actions"><button type="button" title="Editar" onClick={() => startEdit(item)}><FiEdit3 /></button><button type="button" className="is-danger" title="Eliminar" onClick={() => remove(item)}><FiTrash2 /></button></div></div>
                            <strong>{item.subject}</strong>
                            <p>{String(item.body_text || item.body_html || '').slice(0, 220) || 'Sin contenido.'}{String(item.body_text || item.body_html || '').length > 220 ? '…' : ''}</p>
                        </article>
                    ))}
                </div>

                {filtered.length > 0 && <div className="PBADMIN__collection_footer"><div className="PBADMIN__collection_count">Mostrando <strong>{from}–{to}</strong> de <strong>{filtered.length}</strong></div><TablePerPage value={perPage} onChange={setPerPage} totalItems={filtered.length} /><FooterPagination pagina={safePage} totalPaginas={totalPages} onIr={setPage} /></div>}
            </section>
        </EmailSectionLayout>
    );
};
